import { Button, Grid, LinearProgress, Typography } from '@mui/material';
import useProductDrawer from './product-drawer.hook';
import {
  toggleDrawerChildItemOpportunity,
  toggleDrawerParentItemOpportunity
} from '../../store/planner-slice';
import ProductDetailsBox from './product-details-box';
import DrawerHeader from './drawer-header';
import { IAccessConfig } from '../../../common/types/access-configuration.model';
import {
  IProductDetails,
  DrawerProductItem
} from '../../types/ppg-sku-drawer.model';

interface IDrawerComponentProps {
  drawerDetails: IProductDetails;
  drawerData: DrawerProductItem[];
  childDetailsExpandableMap: any;
  handleAddComment: any;
  productId: number;
  appDispatch: any;
  toggleExpansion: (indexOfChild: number) => void;
  handleDeleteComment: any;
  handleUpdateComment: any;
  plannerAccessConfig: IAccessConfig;
  toggledView: string;
  collapseAll: () => void;
  parentDrawerInfo: DrawerProductItem;
}

function DrawerComponent({
  drawerDetails,
  drawerData,
  childDetailsExpandableMap,
  handleAddComment,
  productId,
  appDispatch,
  toggleExpansion,
  handleDeleteComment,
  handleUpdateComment,
  plannerAccessConfig,
  toggledView,
  collapseAll,
  parentDrawerInfo
}: IDrawerComponentProps) {
  return (
    <>
      {drawerData.map((drawerInfo: DrawerProductItem, indexOfChild) => {
        if (drawerInfo.children?.length) {
          return (
            <>
              {drawerInfo.children ? (
                <>
                  <Grid item xs='auto'>
                    <Typography
                      color='#8080CF'
                      fontWeight={700}
                      fontSize='14px'
                    >
                      {
                        drawerDetails.title[
                          drawerInfo.children[0].parent.data.title_key
                        ]
                      }
                    </Typography>
                  </Grid>
                  <Grid
                    style={{ marginBottom: '10px' }}
                    item
                    xs='auto'
                    onClick={collapseAll}
                  >
                    <Button variant='outlined'>Collapse All</Button>
                  </Grid>
                </>
              ) : null}
              <DrawerComponent
                drawerData={drawerInfo.children}
                childDetailsExpandableMap={childDetailsExpandableMap}
                handleAddComment={handleAddComment}
                productId={productId}
                appDispatch={appDispatch}
                collapseAll={collapseAll}
                toggleExpansion={toggleExpansion}
                drawerDetails={drawerDetails}
                handleDeleteComment={handleDeleteComment}
                handleUpdateComment={handleUpdateComment}
                plannerAccessConfig={plannerAccessConfig}
                toggledView={toggledView}
                parentDrawerInfo={parentDrawerInfo}
              />
            </>
          );
        }
        return (
          <Grid
            item
            xs={12}
            mb={1}
            key={drawerInfo.parent.data[drawerInfo.parent.data.key]}
          >
            <ProductDetailsBox
              toggledView={toggledView}
              isPPGBox={false}
              isExpandable
              isOpen={childDetailsExpandableMap[indexOfChild]}
              opportunities={
                drawerDetails.opportunities[drawerInfo.parent.data.key]
              }
              isOpportunitiesSelectionDisabled={
                !plannerAccessConfig?.change_own_opportunity
              }
              productDetails={drawerInfo}
              onCommentAdd={(comment) =>
                // TODO: Add id
                handleAddComment(
                  comment,
                  drawerInfo,
                  parentDrawerInfo,
                  drawerDetails.level_mapping
                )
              }
              onCommentDelete={(commentId) =>
                handleDeleteComment(commentId, drawerInfo, parentDrawerInfo)
              }
              onCommentUpdate={(commentId, updatedComment) =>
                handleUpdateComment(
                  commentId,
                  updatedComment,
                  drawerInfo,
                  parentDrawerInfo,
                  indexOfChild
                )
              }
              toggleExpandableState={() => {
                toggleExpansion(indexOfChild);
              }}
              onOpportunitySelection={(
                isOpportunityChecked,
                selectedOpportunities,
                changedOpportunity
              ) => {
                appDispatch(
                  toggleDrawerChildItemOpportunity({
                    changedOpportunity,
                    isOpportunityChecked,
                    selectedOpportunities,
                    indexOfChild,
                    parentDrawerDetail: parentDrawerInfo,
                    childrenDrawerDetail: drawerInfo
                  })
                );
              }}
            />
          </Grid>
        );
      })}
    </>
  );
}
/**
 * Drawer content for PPG view.
 */
export default function ProductDetailsDrawer() {
  const {
    childDetailsExpandableMap,
    handleAddComment,
    isLoading,
    productId,
    appDispatch,
    collapseAll,
    toggleExpansion,
    handleDeleteComment,
    handleUpdateComment,
    plannerAccessConfig,
    drawerData,
    toggledView
  } = useProductDrawer();

  if (isLoading || !Object.keys(drawerData.opportunities).length) {
    return <LinearProgress />;
  }

  if (Object.keys(drawerData.opportunities).length) {
    return (
      <Grid>
        {drawerData.drawer_data?.map((drawerInfo) => (
          <Grid container key={drawerInfo.parent.data.id}>
            <DrawerHeader
              data-testid='switch-render-container'
              title={drawerData.title[drawerInfo.parent.data.title_key]}
            />
            <Grid item xs={12}>
              <ProductDetailsBox
                isPPGBox
                isExpandable={false}
                toggledView={toggledView}
                opportunities={
                  drawerData.opportunities[drawerInfo.parent.data.key]
                }
                productDetails={drawerInfo}
                isOpen={false}
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                toggleExpandableState={null}
                isOpportunitiesSelectionDisabled={
                  !plannerAccessConfig?.change_own_opportunity
                }
                onCommentDelete={(commentId) =>
                  handleDeleteComment(commentId, drawerInfo, null)
                }
                onCommentUpdate={(commentId, updatedComment) =>
                  handleUpdateComment(
                    commentId,
                    updatedComment,
                    drawerInfo,
                    null
                  )
                }
                onCommentAdd={(comment) =>
                  handleAddComment(
                    comment,
                    drawerInfo,
                    null,
                    drawerData.level_mapping
                  )
                }
                onOpportunitySelection={(
                  isOpportunityChecked,
                  selectedOpportunities,
                  changedOpportunity
                ) => {
                  appDispatch(
                    toggleDrawerParentItemOpportunity({
                      changedOpportunity,
                      isOpportunityChecked,
                      selectedOpportunities,
                      parentDrawerInfo: drawerInfo
                    })
                  );
                }}
              />
            </Grid>
            <Grid
              container
              justifyContent='space-between'
              alignItems='center'
              item
              xs={12}
              my={2}
            >
              {drawerInfo.children?.length ? (
                <>
                  <Grid item xs='auto'>
                    <Typography
                      color='#8080CF'
                      fontWeight={700}
                      fontSize='14px'
                    >
                      {
                        drawerData.title[
                          drawerInfo.children[0].parent.data.title_key
                        ]
                      }
                    </Typography>
                  </Grid>
                  <Grid
                    item
                    xs='auto'
                    style={{ marginBottom: '10px' }}
                    onClick={collapseAll}
                  >
                    <Button variant='outlined'>Collapse All</Button>
                  </Grid>
                </>
              ) : null}
              {drawerInfo.children?.length ? (
                <DrawerComponent
                  drawerData={drawerInfo.children}
                  parentDrawerInfo={drawerInfo}
                  childDetailsExpandableMap={childDetailsExpandableMap}
                  handleAddComment={handleAddComment}
                  productId={productId}
                  drawerDetails={drawerData}
                  appDispatch={appDispatch}
                  toggleExpansion={toggleExpansion}
                  handleDeleteComment={handleDeleteComment}
                  handleUpdateComment={handleUpdateComment}
                  plannerAccessConfig={plannerAccessConfig}
                  toggledView={toggledView}
                  collapseAll={collapseAll}
                />
              ) : null}
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  }

  return <div />;
}
